
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, SaveIcon, BotIcon, CalendarIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { StyleAgentConfig } from '../types';
import { useAppContext } from '../AppContext';

interface StyleAgentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveAgent: (config: StyleAgentConfig) => void;
}

const StyleAgentModal: React.FC<StyleAgentModalProps> = ({ isOpen, onClose, onSaveAgent }) => {
    const { t } = useTranslation();

    // Form State
    const [name, setName] = useState('');
    const [frequency, setFrequency] = useState<'weekly' | 'monthly'>('weekly');
    const [targetSeason, setTargetSeason] = useState('Spring 2025');
    const [focusArea, setFocusArea] = useState('Workwear');
    const [useSavedStyles, setUseSavedStyles] = useState(true);

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        const nextRun = new Date();
        nextRun.setDate(nextRun.getDate() + (frequency === 'weekly' ? 7 : 30));

        const newAgent: StyleAgentConfig = {
            id: `agent-${Date.now()}`,
            name,
            isActive: true,
            frequency,
            targetSeason,
            focusArea,
            nextRun: nextRun.toISOString(),
            retailers: [], // Will be defaulted to profile retailers if none selected
            useSavedStyles
        };

        onSaveAgent(newAgent);

        // Reset form
        setName('');
        setFrequency('weekly');
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg flex flex-col shadow-xl border border-gray-200 dark:border-gray-700"
                    >
                        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 bg-indigo-50 dark:bg-indigo-900/20 rounded-t-2xl">
                            <h2 className="text-xl font-serif font-bold text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
                                <BotIcon className="w-6 h-6" /> {t('styleAgent_modal_title')}
                            </h2>
                            <button onClick={onClose} className="p-1 rounded-full text-gray-500 hover:bg-white dark:hover:bg-gray-700 transition-colors" title={t('common_close')}>
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-5">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                {t('styleAgent_modal_desc')}
                            </p>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('styleAgent_name_label')}</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder={t('styleAgent_name_placeholder')}
                                    className="w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-indigo-500"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('styleAgent_frequency_label')}</label>
                                    <select
                                        value={frequency}
                                        onChange={e => setFrequency(e.target.value as 'weekly' | 'monthly')}
                                        className="w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-indigo-500"
                                        title={t('styleAgent_frequency_label')}
                                    >
                                        <option value="weekly">{t('styleAgent_freq_weekly')}</option>
                                        <option value="monthly">{t('styleAgent_freq_monthly')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('styleAgent_season_label')}</label>
                                    <select
                                        value={targetSeason}
                                        onChange={e => setTargetSeason(e.target.value)}
                                        className="w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-indigo-500"
                                        title={t('styleAgent_season_label')}
                                    >
                                        <option value="Current Season">Current Season</option>
                                        <option value="Spring 2025">Spring 2025</option>
                                        <option value="Summer 2025">Summer 2025</option>
                                        <option value="Fall 2025">Fall 2025</option>
                                        <option value="Winter 2025">Winter 2025</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('styleAgent_focus_label')}</label>
                                <select
                                    value={focusArea}
                                    onChange={e => setFocusArea(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-indigo-500"
                                    title={t('styleAgent_focus_label')}
                                >
                                    <option value="Workwear">Work / Office</option>
                                    <option value="Casual">Weekend / Casual</option>
                                    <option value="Events">Special Events</option>
                                    <option value="Trends">Latest Trends</option>
                                    <option value="Sustainable">Sustainable Fashion</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={useSavedStyles}
                                        onChange={e => setUseSavedStyles(e.target.checked)}
                                        className="sr-only peer"
                                        title={t('styleAgent_useStyles_label')}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                                </label>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('styleAgent_useStyles_label')}</span>
                            </div>

                            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 rounded-xl border border-indigo-100 dark:border-indigo-900/50">
                                <label className="block text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-2">{t('styleAgent_profileRetailers_label')}</label>
                                <div className="flex flex-wrap gap-2">
                                    {useAppContext().userProfile?.preferredRetailers?.map(r => (
                                        <span key={r} className="bg-white dark:bg-gray-800 px-2 py-1 rounded border border-indigo-100 dark:border-indigo-900/30 text-xs text-indigo-700 dark:text-indigo-300">{r}</span>
                                    )) || <span className="text-xs text-gray-500 italic">No retailers set in profile</span>}
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3 border-t border-gray-100 dark:border-gray-700">
                                <button type="button" onClick={onClose} className="px-5 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title={t('common_close')}>
                                    {t('common_close')}
                                </button>
                                <button type="submit" className="flex items-center gap-2 bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-indigo-700 shadow-md">
                                    <SaveIcon className="w-5 h-5" />
                                    {t('styleAgent_create_btn')}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default StyleAgentModal;
